import { useEffect, useRef, useState } from "react";

import { Bot, Brain, Eye, Radar, Search, Sparkles, Users, Zap } from "lucide-react";

const WITTY_MESSAGES = [
  { text: "Asking ChatGPT what kind of traveler would love your business…", icon: Search },
  { text: "Gemini is crafting the perfect couple's getaway search…", icon: Brain },
  { text: "Claude is evaluating your family-friendliness, very thoroughly…", icon: Bot },
  { text: "Checking if solo travelers even know you exist…", icon: Users },
  { text: "Counting your mentions across three AI brains…", icon: Sparkles },
  { text: "Business travellers are picky — let's see how you score…", icon: Radar },
  { text: "Running persona queries faster than a travel agent on espresso…", icon: Zap },
  { text: "Convincing three AIs to agree on your best guest type…", icon: Eye },
  { text: "Scoring your business for every traveller that's ever existed…", icon: Search },
  { text: "Our AI panel is deliberating your persona visibility…", icon: Brain },
];

const LLM_BADGES = [
  { name: "ChatGPT", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  { name: "Gemini", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { name: "Claude", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
];

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const PersonaLoaderDialog = ({ personaCount }) => {
  const [elapsed, setElapsed] = useState(0);
  const [msgIndex, setMsgIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % WITTY_MESSAGES.length);
        setIsTransitioning(false);
      }, 400);
    }, 10000);
    return () => clearInterval(interval);
  }, [msgIndex]);

  const currentMsg = WITTY_MESSAGES[msgIndex];
  const CurrentIcon = currentMsg.icon;

  return (
    <div className="audit-loader-overlay">
      <div className="audit-loader-card">
        {/* Spinner */}
        <div className="audit-spinner-container">
          <div className="audit-spinner-ring audit-ring-1" />
          <div className="audit-spinner-ring audit-ring-2" />
          <div className="audit-spinner-ring audit-ring-3" />
          <div className="audit-spinner-core">
            <div className="flex gap-1">
              {LLM_BADGES.map((b) => (
                <span
                  key={b.name}
                  className={`text-[8px] font-bold px-1 py-0.5 rounded border ${b.color}`}
                >
                  {b.name.charAt(0)}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Phase badge */}
        <div className="audit-phase-badge">
          <span className="audit-phase-dot" />
          Scanning {personaCount} Persona{personaCount !== 1 ? "s" : ""} across 3 AIs
        </div>

        <h2 className="audit-loader-title">Running Persona Visibility Scan</h2>
        <p className="audit-loader-subtitle">
          We&apos;re querying ChatGPT, Gemini, and Claude for each traveler segment and scoring your
          business&apos;s visibility.
        </p>

        {/* LLM Provider Badges */}
        <div className="flex items-center justify-center gap-2 mt-2 mb-2">
          {LLM_BADGES.map((badge) => (
            <span
              key={badge.name}
              className={`text-xs font-semibold px-3 py-1 rounded-full border ${badge.color}`}
            >
              {badge.name}
            </span>
          ))}
        </div>

        {/* Cycling witty message */}
        <div className="audit-witty-container">
          <div
            className={`audit-witty-msg ${isTransitioning ? "audit-witty-exit" : "audit-witty-enter"}`}
          >
            <CurrentIcon className="w-4 h-4 text-primary shrink-0" />
            <span>{currentMsg.text}</span>
          </div>
        </div>

        {/* Progress dots */}
        <div className="audit-progress-dots">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="audit-dot" style={{ animationDelay: `${i * 0.2}s` }} />
          ))}
        </div>

        {/* Elapsed */}
        <div className="audit-elapsed">
          <span className="audit-elapsed-label">Elapsed</span>
          <span className="audit-elapsed-time">{formatTime(elapsed)}</span>
        </div>

        {/* ETA */}
        <div className="text-xs text-on-surface-variant mt-2 font-medium">
          Estimated time: <span className="text-primary">1-3 min</span>
        </div>
      </div>
    </div>
  );
};

export default PersonaLoaderDialog;
